import { useState, useEffect } from 'react'
import { BREAKPOINTS } from '../constants/breakpoints'

/**
 * Dead-band (px) applied around breakpoint thresholds to prevent layout
 * oscillation when the container width hovers near a boundary.
 *
 * Typical cause: switching layout causes a scrollbar to appear or disappear,
 * which shifts the container width by ~17 px and immediately triggers the
 * opposite transition — creating an infinite flip-flop.
 *
 * With hysteresis the effective threshold depends on the current layout:
 * - To LEAVE desktop the width must drop below  TABLET_L - HYSTERESIS
 * - To ENTER desktop the width must rise above  TABLET_L
 * - To LEAVE mobile  the width must rise above  MOBILE_L + HYSTERESIS
 * - To ENTER mobile  the width must drop below  MOBILE_L
 */
const HYSTERESIS = 20

/**
 * Classifies a pixel width into a device layout category, applying hysteresis
 * based on the previous layout to avoid oscillation near breakpoint boundaries.
 *
 * Thresholds (from BREAKPOINTS):
 * - isDesktop: width > TABLET_L (1179px)
 * - isTablet:  width > MOBILE_L (767px) and ≤ TABLET_L (1179px)
 * - isMobile:  width ≤ MOBILE_L (767px)
 *
 * Tablets and touch-screen laptops intentionally get desktop layout.
 * Mobile layout is reserved for small screens only.
 *
 * @param {number} width
 * @param {{ isDesktop: boolean, isMobile: boolean } | null} prev - Previous layout state
 * @returns {{ isDesktop: boolean, isTablet: boolean, isMobile: boolean, containerWidth: number }}
 */
function classifyWidth(width, prev = null) {
  // When already in desktop, require dropping HYSTERESIS px below the threshold
  // before switching away — prevents flip-flop caused by scrollbar appearing.
  const isDesktop = prev?.isDesktop
    ? width > BREAKPOINTS.TABLET_L - HYSTERESIS
    : width > BREAKPOINTS.TABLET_L

  // Symmetrical guard for the mobile boundary.
  const isMobile = prev?.isMobile
    ? width <= BREAKPOINTS.MOBILE_L + HYSTERESIS
    : width <= BREAKPOINTS.MOBILE_L

  return {
    isDesktop,
    isTablet: !isDesktop && !isMobile,
    isMobile,
    containerWidth: width,
  }
}

/**
 * Returns responsive layout state derived from the actual rendered width
 * of the app's root container element.
 *
 * Uses ResizeObserver on the provided ref — intentionally NOT window.innerWidth —
 * making the app resilient to pages without proper viewport meta (e.g. legacy WP themes
 * that render the page at 980px and then scale it down on mobile). In such cases,
 * window.innerWidth reports 980px regardless of the physical screen size, causing
 * desktop layout to be served on phones.
 *
 * By observing the container element's real rendered width, the hook reflects the
 * actual space available to the app, not the browser viewport.
 *
 * The app must be declared as a CSS container (container-type: inline-size) for SCSS
 * @container queries to work alongside this hook.
 *
 * @param {React.RefObject<HTMLElement>} containerRef - Ref to the app root container element
 * @returns {{ isDesktop: boolean, isTablet: boolean, isMobile: boolean, containerWidth: number }}
 */
export function useDeviceLayout(containerRef) {
  const [layout, setLayout] = useState(() => classifyWidth(window.innerWidth))

  useEffect(() => {
    const el = containerRef?.current
    if (!el) return

    const observer = new ResizeObserver(([entry]) => {
      setLayout(prev => classifyWidth(entry.contentRect.width, prev))
    })

    observer.observe(el)
    // Classify immediately based on current rendered size (before next resize event)
    setLayout(prev => classifyWidth(el.getBoundingClientRect().width, prev))

    return () => observer.disconnect()
  }, [containerRef])

  return layout
}
